import { observer } from 'mobx-react-lite';
import { useStore } from '../../../../hooks/useStore';
import { Form, Formik } from 'formik';
import CommonButton from '../../../../components/common-components/button/CommonButton';
import { FormikTextField } from '../../../../components/form-components/common-components/text-field/FormikTextField';
import { FormikFileUpload } from '../../../../components/form-components/common-components/file-upload/FormikFileUpload';
import Popup from '../../../../components/common-components/pop-up/Popup';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import styles from './NewExpensePopup.module.css';
import { Currency } from 'lucide-react';

const validationSchema = Yup.object({
  amount: Yup.number()
    .required('Miktar zorunludur')
    .positive("Miktar 0'dan büyük olmalıdır")
    .min(0.01, 'Miktar en az 0.01 olmalıdır'),
  expenseDate: Yup.date().required('Tarih zorunludur'),
  description: Yup.string()
    .required('Açıklama zorunludur')
    .min(3, 'Açıklama en az 3 karakter olmalıdır'),
  files: Yup.array().nullable(),
});

const initialValues = {
  amount: '',
  expenseDate: new Date().toISOString().split('T')[0],
  description: '',
  files: [] as File[],
};

const NewExpensePopup = () => {
  const { popupStore, expenseStore, authStore } = useStore();

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      const formData = new FormData();
      formData.append('amount', values.amount.toString());
      formData.append('expenseDate', new Date(values.expenseDate).toISOString());
      formData.append('description', values.description);

      if (values.files && values.files.length > 0) {
        values.files.forEach(file => {
          formData.append('files', file);
        });
      }

      const response = await expenseStore.createExpense(formData);

      if (!response.error) {
        popupStore.setIsNewExpenseOpen(false);
      }
    } catch (error) {
      console.error('Error creating expense:', error);
      toast.error('Harcama kaydı oluşturulurken bir hata oluştu');
    }
  };

  const handleClose = () => {
    popupStore.setIsNewExpenseOpen(false);
  };

  return (
    <Popup isOpen={popupStore.isNewExpenseOpen} onClose={handleClose} title="Yeni Harcama Kaydı">
      <div className={styles.container}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values }) => (
            <Form className={styles.form}>
              <div className={styles.inputContainer}>
                <FormikTextField
                  name="amount"
                  label="Miktar (₺)"
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  startAdornment={<Currency size={18} className={styles.currencyIcon} />}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField name="expenseDate" label="Harcama Tarihi" type="date" />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="description"
                  label="Açıklama"
                  placeholder="Harcama açıklamasını giriniz..."
                  type="text"
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikFileUpload
                  name="files"
                  label="Dosyalar (İsteğe Bağlı)"
                  placeholder="Dosya seçin veya buraya sürükleyin"
                  accept=".pdf,.png,.jpeg,.jpg"
                  multiple={true}
                  maxSize={2} // 2MB
                  maxFiles={3} // Maximum 3 files
                  allowedTypes={['pdf', 'png', 'jpeg', 'jpg']}
                />
              </div>

              <div className={styles.buttonContainer}>
                <CommonButton type="secondary" onClick={handleClose}>
                  İptal
                </CommonButton>
                <CommonButton
                  type="primary"
                  disabled={isSubmitting}
                  className={styles.submitButton}
                >
                  {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
                </CommonButton>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Popup>
  );
};

export default observer(NewExpensePopup);
